import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Capacitor } from "@/lib/capacitor";
import { StatusBar } from "@/lib/capacitor/status-bar";
import { useAppearance } from "@/lib/hooks/use-appearance";
import { queryOptions_theme } from "@/lib/providers/query/functions/theme";
import { ThemeContext } from "@/lib/providers/theme/context";
import { type ResolvedAppearance, cacheAppearance, clearCachedAppearance } from "@/lib/theme/boot";
import { deriveDarkTheme } from "@/lib/theme/dark";
import { withReadableTones } from "@/lib/theme/readable";
import { DEFAULT_THEME } from "@/lib/theme/themes/default";
import { type Theme } from "@/lib/theme/type";
import {
  appendRgbToCssVars,
  convertThemeToCssVars,
  convertThemeToIonicStepCssVars,
} from "@/lib/theme/utils";
import { logger } from "@/logger";
import { type CssVars, useCssVars } from "@/utils/css-vars";

export function ThemeProvider(props: React.PropsWithChildren) {
  const query = useQuery(
    queryOptions_theme({
      slug: import.meta.env.VITE_ORGANIZATION_SLUG ?? "default",
    }),
  );
  const { resolved: appearance, available } = useAppearance();

  const brandTheme = useMemo(() => {
    const themeConfig = query.data as { config?: Theme } | Theme | undefined;

    let baseTheme: Theme | undefined;

    // If the API response has a `config` property, extract it to flatten the structure
    // This ensures CSS variables are created as --color-* instead of --config-color-*
    if (themeConfig && "config" in themeConfig && themeConfig.config) {
      baseTheme = themeConfig.config;
    } else {
      // Otherwise, treat the response as the theme directly
      baseTheme = themeConfig as Theme | undefined;
    }

    // If no theme from API, use default
    if (!baseTheme) {
      return DEFAULT_THEME;
    }

    // Merge with defaults to ensure all required properties exist
    // This handles cases where backend hasn't been updated yet with new theme properties
    return {
      color: {
        ...baseTheme.color,
        // If button colors are missing, use txt.brand as fallback for button.primary
        button: baseTheme.color.button || {
          primary: baseTheme.color.txt?.brand || DEFAULT_THEME.color.button.primary,
        },
      },
    } as Theme;
  }, [query.data]);

  /**
   * The dark appearance is derived from the brand's own light theme rather than authored, so a
   * brand that has never been seen still gets its own night instead of Boomer's. See
   * `lib/theme/dark.ts`.
   */
  const theme = useMemo(
    () => (appearance === "dark" ? deriveDarkTheme(brandTheme) : brandTheme),
    [brandTheme, appearance],
  );

  // `withReadableTones` adds the derived `120` step per semantic ramp (issue #99). Brands only
  // supply `10 / 20 / 80 / 100`, and `100` — the only dark step — is a surface/icon colour, not a
  // readable one. Deriving here rather than hardcoding keeps every white-label brand in its own
  // tint instead of inheriting Boomer's.
  const cssVars = useMemo(
    () => ({
      ...appendRgbToCssVars(convertThemeToCssVars(withReadableTones(theme))),
      ...convertThemeToIonicStepCssVars(theme),
    }),
    [theme],
  );

  useCssVars(cssVars);
  useAppearanceChrome(appearance, available, theme, cssVars);

  const value = useMemo(() => ({ theme, brandTheme, appearance }), [theme, brandTheme, appearance]);

  return <ThemeContext.Provider value={value}>{props.children}</ThemeContext.Provider>;
}

/**
 * Everything outside the React tree that has to agree with the appearance.
 *
 * The CSS variables above paint the app; none of them reach the frame around it. The status bar
 * belongs to the OS, the scrollbars and native controls belong to the engine, and the first frame
 * of the next cold start belongs to nobody yet. A dark app inside a white system chrome is the
 * detail that makes the whole thing look unfinished, so it is handled in the same place and at the
 * same moment as the colours.
 */
function useAppearanceChrome(
  appearance: ResolvedAppearance,
  available: boolean,
  theme: Theme,
  cssVars: CssVars,
) {
  const background = theme.color?.surface?.background ?? DEFAULT_THEME.color.surface.background;

  useCrossFade(appearance);

  useEffect(() => {
    const root = document.documentElement;

    root.classList.toggle("dark", appearance === "dark");

    // Not decoration: `color-scheme` is what makes the engine paint form controls, the caret, the
    // scrollbars and — critically on iOS — the rubber-band overscroll area in the right colour.
    // Without it the app is dark and the space above a bounced scroll is still white.
    root.style.colorScheme = appearance;

    return () => {
      root.classList.remove("dark");
      root.style.removeProperty("color-scheme");
    };
  }, [appearance]);

  useEffect(() => {
    themeColorMeta().setAttribute("content", background);
  }, [background]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // `Style.Dark` reads backwards: it means "light content", for a dark background. Getting this
    // the wrong way round paints the clock and the battery in the same colour as the bar.
    const style = appearance === "dark" ? StatusBar.Style.Dark : StatusBar.Style.Light;

    // Fire and forget, and never let it take the app down with it: on a device where the status bar
    // is hidden or the plugin is unavailable these reject, and an unstyled status bar is a blemish,
    // not a reason to lose the screen.
    StatusBar.setStyle(style).catch((error) => logger.warn("Status bar style failed", { error }));

    // Android draws its own status bar background; iOS takes the colour from the page underneath.
    if (Capacitor.Platform === "android") {
      StatusBar.setBackgroundColor(background).catch((error) =>
        logger.warn("Status bar background failed", { error }),
      );
    }
  }, [appearance, background]);

  useEffect(() => {
    // What the inline script in `index.html` paints before React exists on the next cold start.
    // Written after the fact rather than predicted, so the cache can only ever be one launch stale
    // — and is dropped entirely when the beta ends, so an app without a dark appearance cannot
    // start in one.
    if (available) {
      cacheAppearance(appearance, cssVars);
    } else {
      clearCachedAppearance();
    }
  }, [appearance, available, cssVars]);
}

/** Kept in step with the transition in `styles/tailwind/base.css`. */
const CROSS_FADE_MS = 220;

/**
 * Let the switch read as a change of light rather than a cut.
 *
 * Deliberately not on the first paint: the app appearing out of a fade every time it is opened
 * would be slower than opening it, and the whole point of the boot cache is that the first frame is
 * already correct. So the fade only exists between two appearances the employee actually moved
 * between.
 */
function useCrossFade(appearance: ResolvedAppearance) {
  const previous = useRef<ResolvedAppearance | undefined>(undefined);

  useEffect(() => {
    const changed = previous.current !== undefined && previous.current !== appearance;
    previous.current = appearance;

    if (!changed) {
      return;
    }

    const root = document.documentElement;
    root.classList.add("appearance-transition");

    const timeout = window.setTimeout(
      () => root.classList.remove("appearance-transition"),
      CROSS_FADE_MS,
    );

    return () => {
      window.clearTimeout(timeout);
      root.classList.remove("appearance-transition");
    };
  }, [appearance]);
}

/**
 * The browser and Android task switcher tint their own chrome from this, and it is not in the
 * document — the head is generated per brand, so the tag is created on demand rather than assumed.
 */
function themeColorMeta(): HTMLMetaElement {
  const existing = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');

  if (existing) {
    return existing;
  }

  const meta = document.createElement("meta");
  meta.name = "theme-color";
  document.head.appendChild(meta);

  return meta;
}
